import { api } from './api';

// Alerts when the game is closed (web push), from the player's side: what this browser can do, and switching
// alerts on or off for it. On an iPhone they only work once the game is on the Home Screen (iOS 16.4+).
export type PushState = 'unsupported' | 'install' | 'blocked' | 'off' | 'on';

const supported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

async function registration() {
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}
const toKey = (base64url: string) => {
  const raw = atob(base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !standalone() ? 'install' : 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  const existing = await navigator.serviceWorker.getRegistration('/');
  const subscription = await existing?.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

// asks the player (the browser's own prompt), then hands the subscription to the server
export async function turnPushOn(): Promise<PushState> {
  if (!supported()) return isIos() && !standalone() ? 'install' : 'unsupported';
  const { publicKey } = await api.pushKey();
  if (!publicKey) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  const worker = await registration();
  await navigator.serviceWorker.ready;
  const subscription =
    (await worker.pushManager.getSubscription()) ??
    (await worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(publicKey) }));
  const json = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
  return 'on';
}

export async function turnPushOff(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  const existing = await navigator.serviceWorker.getRegistration('/');
  const subscription = await existing?.pushManager.getSubscription();
  if (subscription) {
    await api.pushUnsubscribe(subscription.endpoint).catch(() => {});
    await subscription.unsubscribe();
  }
  return 'off';
}

// on each visit: keep the server's copy of this browser's subscription current (it can change)
export async function refreshPush() {
  if ((await pushState()) !== 'on') return;
  const existing = await navigator.serviceWorker.getRegistration('/');
  const subscription = await existing?.pushManager.getSubscription();
  if (!subscription) return;
  const json = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
}

// the game open in a tab you are not looking at: the alert shows like any other
export async function showLocalAlert(title: string, body: string, url: string, tag: string) {
  if (!supported() || Notification.permission !== 'granted') return false;
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) return false;
  await existing.showNotification(title, { body, tag, icon: '/icon-192.png', data: { url } });
  return true;
}
